/**
 * Video Editor — AI co-pilot panel for surfaces 3-7 (Phase 4, spec §5.8).
 *
 * One component, two visual states: a collapsed pill and an expanded panel.
 * Per-surface default — S5 opens expanded (refine is where the co-pilot is
 * primary), the rest start collapsed. On S5 an `edit_plan_suggestion` can be
 * accepted, which fires the existing PATCH /edit_plan.
 *
 * Wired to the agent substrate via POST /projects/:id/copilot/turn.
 */
import React, { useState, useEffect } from 'react';
import type { VideoProject, EditPlan } from '../types';
import type { VideoEditorApi } from '../videoEditorApi';

interface ChatMsg { role: 'user' | 'bot'; text: string; }
interface Props {
  project: VideoProject;
  surface: string;            // 'S3' .. 'S7'
  api: VideoEditorApi;
  onRefresh: () => Promise<any>;
}

// Surfaces where the co-pilot opens expanded by default (spec §5.8).
const EXPANDED_BY_DEFAULT: Record<string, boolean> = { S5: true };

/**
 * Apply a co-pilot edit-plan change to a plan. Handles the common ops;
 * PATCH /edit_plan fires regardless of op (an unknown op leaves the plan).
 */
function applyChange(plan: EditPlan, change: any): EditPlan {
  const next: EditPlan = { ...plan, segments: (plan.segments || []).map(s => ({ ...s })) };
  if (!change || typeof change !== 'object') return next;
  const i = typeof change.segment === 'number' ? change.segment : -1;
  const segs = next.segments;
  if (change.op === 'trim' && segs[i]) {
    if (typeof change.in_point_ms === 'number') segs[i].in_point_ms = change.in_point_ms;
    if (typeof change.out_point_ms === 'number') segs[i].out_point_ms = change.out_point_ms;
  } else if (change.op === 'remove' && segs[i]) {
    next.segments = segs.filter((_, idx) => idx !== i);
  } else if (change.op === 'reorder' && segs[i] && typeof change.to === 'number') {
    const [moved] = next.segments.splice(i, 1);
    next.segments.splice(change.to, 0, moved);
  }
  return next;
}

const AICoPilot: React.FC<Props> = ({ project, surface, api, onRefresh }) => {
  const [expanded, setExpanded] = useState(!!EXPANDED_BY_DEFAULT[surface]);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [suggestion, setSuggestion] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Re-apply the per-surface default + reset when the surface changes.
  useEffect(() => {
    setExpanded(!!EXPANDED_BY_DEFAULT[surface]);
    setMessages([]); setSuggestion(null); setErr(null);
  }, [surface]);

  async function send(text: string) {
    const t = text.trim();
    if (!t || busy) return;
    const history = messages.map(m => ({
      role: m.role === 'bot' ? 'assistant' : 'user', text: m.text
    }));
    setMessages(m => [...m, { role: 'user', text: t }]);
    setInput(''); setBusy(true); setErr(null); setSuggestion(null);
    try {
      const res = await api.copilotTurn(project._id, surface, t, history);
      const out = res.output || {};
      const text2 = out.message || (out.card && out.card.text) || '(no reply)';
      setMessages(m => [...m, { role: 'bot', text: text2 }]);
      if (out.kind === 'edit_plan_suggestion' && out.card) {
        if (res.validation && res.validation.status !== 'passed') {
          setErr('The suggestion was routed to review by the validation gate.');
        } else {
          setSuggestion(out.card);
        }
      } else if (out.route_to_reviewer) {
        setErr('The co-pilot routed this question to a human reviewer.');
      }
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Co-pilot turn failed');
    } finally { setBusy(false); }
  }

  async function acceptSuggestion() {
    if (!suggestion || !suggestion.change) return;
    try {
      setBusy(true); setErr(null);
      const plan: EditPlan = (project.editPlan && Array.isArray(project.editPlan.segments))
        ? project.editPlan : { segments: [] };
      await api.patchEditPlan(project._id, applyChange(plan, suggestion.change));
      setMessages(m => [...m, { role: 'bot', text: '✓ Applied to the edit plan. Re-render to see it.' }]);
      setSuggestion(null);
      await onRefresh();
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Could not apply the suggestion');
    } finally { setBusy(false); }
  }

  if (!expanded) {
    return (
      <button className="ve-copilot-pill" onClick={() => setExpanded(true)}>
        ✦ Co-pilot
      </button>
    );
  }

  return (
    <div className="ve-copilot-panel">
      <div className="ve-copilot-panel__head">
        <strong>✦ Co-pilot</strong>
        <span className="ve-muted">· {surface}</span>
        <button className="ve-iconbtn" style={{ marginLeft: 'auto' }}
          title="Collapse" onClick={() => setExpanded(false)}>—</button>
      </div>
      {err && <div className="ve-banner ve-banner--error">{err}</div>}
      <div className="ve-chat ve-chat--panel">
        {messages.length === 0 && (
          <div className="ve-msg ve-msg--bot ve-muted">
            Ask about the cut, or how to refine it.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`ve-msg ve-msg--${m.role}`}>{m.text}</div>
        ))}
        {busy && <div className="ve-msg ve-msg--bot ve-muted">thinking…</div>}
      </div>
      {suggestion && (
        <div className="ve-suggestion">
          <strong>{suggestion.label || 'Suggestion'}</strong>
          <p className="ve-muted">{suggestion.text}</p>
          <div className="ve-actions">
            <button className="ve-btn ve-btn--primary" disabled={busy}
              onClick={acceptSuggestion}>Accept</button>
            <button className="ve-btn" disabled={busy}
              onClick={() => setSuggestion(null)}>Dismiss</button>
          </div>
        </div>
      )}
      <div className="ve-chat-input">
        <input
          className="ve-textinput"
          value={input}
          disabled={busy}
          placeholder="Ask the co-pilot…"
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(input); }}
        />
        <button className="ve-btn ve-btn--primary" disabled={busy || !input.trim()}
          onClick={() => send(input)}>Send</button>
      </div>
    </div>
  );
};

export default AICoPilot;
